import { evaluateRuntimeHealth } from './RuntimeHealthEvaluator';
import { freezeSanitizedSnapshot } from './sanitize';
import type { PixelDebugSnapshot, SnapshotCollectContext } from './types';

export function buildDebugSnapshot(
  ctx: SnapshotCollectContext
): Readonly<PixelDebugSnapshot> {
  const services = ctx.services;
  const transportState = ctx.transport.state;

  const snapshot: PixelDebugSnapshot = {
    sdkVersion: ctx.sdkVersion,
    capturedAt: Date.now(),
    runtime: {
      initialized: ctx.runtimeInitialized,
      destroyed: ctx.runtimeDestroyed,
      moduleCount: ctx.moduleRegistry.length,
      activeModuleCount: ctx.moduleRegistry.filter(m => m.initialized).length,
      moduleNames: ctx.moduleRegistry.map(m => m.name),
      activeModuleNames: ctx.moduleRegistry
        .filter(m => m.initialized)
        .map(m => m.name),
    },
    modules: ctx.moduleRegistry.map(m => ({
      id: m.name,
      configuredEnabled: m.configuredEnabled,
      initialized: m.initialized,
    })),
    core: {
      enabled: ctx.moduleRegistry.some(
        m => m.name === 'core' && m.configuredEnabled
      ),
      visitorId: services?.visitorId ?? null,
      sessionId: services?.sessionId ?? null,
      sessionExpired: services?.sessionExpired ?? null,
      fingerprint: services?.fingerprint ?? null,
      urlContextEnabled: services?.urlContextEnabled ?? false,
    },
    urlContext: services?.urlContextSnapshot
      ? {
          enabled: services.urlContextEnabled,
          available: true,
          managedKeys: Object.keys(services.urlContextSnapshot.managed),
          forwardedKeys: Object.keys(services.urlContextSnapshot.forwarded),
          source: { ...services.urlContextSnapshot.source },
          queryParamCount: services.urlContextSnapshot.queryParamCount,
        }
      : {
          enabled: false,
          available: false,
          managedKeys: [],
          forwardedKeys: [],
          source: {},
          queryParamCount: 0,
        },
    attribution: {
      enabled: services?.attributionEnabled ?? false,
      available: services?.attributionIdentifiers != null,
      identifierKeys: services?.attributionIdentifiers
        ? Object.keys(services.attributionIdentifiers)
        : [],
      identifiers: services?.attributionIdentifiers
        ? { ...services.attributionIdentifiers }
        : {},
    },
    commerce: {
      enabled: services?.commerceEnabled ?? false,
      available: services != null,
      mode: services?.commerceMode ?? null,
      currency: services?.commerceCurrency ?? null,
      catalogSize: services?.catalogSize ?? 0,
      itemCount: services?.itemCount ?? 0,
      cartValue: services?.cartValue ?? null,
      productIds: services?.productIds ?? [],
    },
    behavior: {
      enabled: ctx.behaviorEnabled,
      configuredTrackers: [...ctx.behaviorTrackers],
      configuredEngines: [...ctx.behaviorEngines],
      heartbeat: ctx.behaviorHeartbeat
        ? {
            enabled: ctx.behaviorHeartbeat.enabled,
            initialized: ctx.behaviorHeartbeat.initialized,
            running: ctx.behaviorHeartbeat.running,
            intervalMs: ctx.behaviorHeartbeat.intervalMs,
            hiddenIntervalMs: ctx.behaviorHeartbeat.hiddenIntervalMs,
            sequence: ctx.behaviorHeartbeat.sequence,
            emittedCount: ctx.behaviorHeartbeat.emittedCount,
            skippedCount: ctx.behaviorHeartbeat.skippedCount,
            lastEmittedAt: ctx.behaviorHeartbeat.lastEmittedAt,
            nextExpectedAt: ctx.behaviorHeartbeat.nextExpectedAt,
            lastDriftMs: ctx.behaviorHeartbeat.lastDriftMs,
          }
        : null,
      viewport: ctx.behaviorViewport
        ? {
            enabled: ctx.behaviorViewport.enabled,
            initialized: ctx.behaviorViewport.initialized,
            available: ctx.behaviorViewport.available,
            targetCount: ctx.behaviorViewport.targetCount,
            matchedElementCount: ctx.behaviorViewport.matchedElementCount,
            observedElementCount: ctx.behaviorViewport.observedElementCount,
            activeElementCount: ctx.behaviorViewport.activeElementCount,
            pendingElementCount: ctx.behaviorViewport.pendingElementCount,
            enterCount: ctx.behaviorViewport.enterCount,
            exitCount: ctx.behaviorViewport.exitCount,
            mutationObserverEnabled:
              ctx.behaviorViewport.mutationObserverEnabled,
            maxObservedElements: ctx.behaviorViewport.maxObservedElements,
            limitReached: ctx.behaviorViewport.limitReached,
            lastEventAt: ctx.behaviorViewport.lastEventAt,
          }
        : null,
      engagement: ctx.behaviorEngagement
        ? {
            enabled: ctx.behaviorEngagement.enabled,
            initialized: ctx.behaviorEngagement.initialized,
            running: ctx.behaviorEngagement.running,
            score: ctx.behaviorEngagement.score,
            previousScore: ctx.behaviorEngagement.previousScore,
            peakScore: ctx.behaviorEngagement.peakScore,
            averageScore: ctx.behaviorEngagement.averageScore,
            level: ctx.behaviorEngagement.level,
            evaluationCount: ctx.behaviorEngagement.evaluationCount,
            emittedUpdateCount: ctx.behaviorEngagement.emittedUpdateCount,
            emittedSummaryCount: ctx.behaviorEngagement.emittedSummaryCount,
            lastEvaluatedAt: ctx.behaviorEngagement.lastEvaluatedAt,
            lastEmittedAt: ctx.behaviorEngagement.lastEmittedAt,
            evaluationIntervalMs: ctx.behaviorEngagement.evaluationIntervalMs,
            lastDriftMs: ctx.behaviorEngagement.lastDriftMs,
            hasError: ctx.behaviorEngagement.hasError,
            signalCounts: { ...ctx.behaviorEngagement.signalCounts },
          }
        : null,
      rateLimit: ctx.behaviorRateLimit ? { ...ctx.behaviorRateLimit } : null,
      buffer: ctx.behaviorBuffer ? { ...ctx.behaviorBuffer } : null,
      stateMachine: ctx.behaviorStateMachine
        ? { ...ctx.behaviorStateMachine }
        : null,
      sessionSummary: ctx.behaviorSessionSummary
        ? { ...ctx.behaviorSessionSummary }
        : null,
    },
    conversion: {
      enabled: ctx.conversion.enabled,
      autoEnabled: ctx.conversion.autoEnabled,
      manualEnabledFlags: { ...ctx.conversion.manualEnabledFlags },
      detectorCount: ctx.conversion.detectorCount,
      activeDetectors: [...ctx.conversion.activeDetectors],
      dedupeKeyCount: ctx.conversion.dedupeKeyCount,
    },
    integrations: {
      enabled: ctx.integrations.enabled,
      available: true,
      active: ctx.integrations.active,
      destinationNames: [...ctx.integrations.destinationNames],
      metrics: ctx.integrations.metrics
        ? { ...ctx.integrations.metrics }
        : null,
    },
    transport: transportState
      ? {
          enabled: ctx.transport.enabled,
          available: true,
          online: transportState.online,
          senderMode: transportState.senderMode,
          queueSize: transportState.queueSize,
          sentCount: transportState.sentCount,
          ackCount: transportState.ackCount,
          inFlight: transportState.inFlight,
          retryPending: transportState.retryPending,
          circuitBreakerState: transportState.circuitBreakerState,
          dlqCount: transportState.dlqCount,
          isLeader: transportState.isLeader,
          persistenceEnabled: transportState.persistenceEnabled,
        }
      : {
          enabled: ctx.transport.enabled,
          available: false,
          online: 'unknown',
          senderMode: ctx.transport.enabled ? 'unavailable' : 'unknown',
          queueSize: 'unavailable',
          sentCount: 'unavailable',
          ackCount: 'unavailable',
          inFlight: 'unavailable',
          retryPending: 'unavailable',
          circuitBreakerState: 'unavailable',
          dlqCount: 'unavailable',
          isLeader: 'unavailable',
          persistenceEnabled: 'unavailable',
        },
    health: evaluateRuntimeHealth(ctx),
    warnings: buildWarnings(ctx),
    errors: [],
    config: ctx.configSafe,
  };

  return freezeSanitizedSnapshot(snapshot);
}

function buildWarnings(ctx: SnapshotCollectContext) {
  const warnings: Array<{
    code: string;
    message: string;
    source?: string;
  }> = [];
  if (!ctx.runtimeInitialized) {
    warnings.push({
      code: 'RUNTIME_NOT_INITIALIZED',
      message: 'Snapshot captured before init()',
      source: 'runtime',
    });
  }
  if (ctx.runtimeDestroyed) {
    warnings.push({
      code: 'RUNTIME_DESTROYED',
      message: 'Snapshot captured after destroy()',
      source: 'runtime',
    });
  }
  if (ctx.transport.enabled && !ctx.transport.state) {
    warnings.push({
      code: 'TRANSPORT_STATE_UNAVAILABLE',
      message: 'Transport enabled but debug state unavailable',
      source: 'transport',
    });
  }
  return warnings;
}
